/**
 * Is this an INTERNAL (staff) identity? Reads `pem:user_type` from the issuer
 * id_token without verifying it — svc-02 has already verified the same token
 * server-side, and this only chooses which message to show, never what is
 * allowed. A malformed token yields false, i.e. the customer wording.
 */
export function isInternalIdentity(idToken: string): boolean {
  return decodeClaims(idToken)?.["pem:user_type"] === "internal";
}

/**
 * Has this issuer id_token's `exp` passed? Only a decodable, numeric `exp`
 * that is already in the past answers true. A malformed token or one without
 * `exp` answers false and is left for svc-02 to judge, so this can only ever
 * SKIP a round trip, never admit a token. `exp` is compared with no leeway:
 * a token expired by seconds is sent to re-login even where svc-02's own
 * verification might still have accepted it. Narrow window, safe direction.
 */
export function isIssuerIdTokenExpired(
  idToken: string,
  nowMs: number = Date.now(),
): boolean {
  const exp = decodeClaims(idToken)?.exp;
  return typeof exp === "number" && exp * 1000 <= nowMs;
}

/** Unverified payload decode; null for anything that is not a JWT shape. */
function decodeClaims(idToken: string): Record<string, unknown> | null {
  try {
    const [, payload] = idToken.split(".");
    if (!payload) return null;
    return JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>;
  } catch {
    return null;
  }
}
